import { useCallback, useEffect, useState } from "react";

/** Light, dark, or follow the OS. Light is the DS « sepia » theme (warm page, brown accent, as in the
 * DAR's committee tool); dark is the DS dark theme. Both activate on `<html data-theme>`. */
export type ThemeMode = "light" | "dark" | "system";

const KEY = "ada.theme";

function prefersDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function storedTheme(): ThemeMode {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === "light" || value === "dark" || value === "system" ? value : "system";
  } catch {
    return "system";
  }
}

export function applyTheme(mode: ThemeMode): void {
  const dark = mode === "dark" || (mode === "system" && prefersDark());
  document.documentElement.dataset.theme = dark ? "dark" : "sepia";
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(storedTheme);

  useEffect(() => {
    applyTheme(mode);
    if (mode !== "system" || typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme("system");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      // Private mode: the choice lasts for the session only.
    }
    setModeState(next);
  }, []);

  return { mode, setMode };
}
